import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  CCol, CRow, CForm, CFormInput, CFormLabel, CFormSelect, CButton, CAlert, CSpinner, CBadge, CTooltip,
  CCard, CCardHeader, CCardBody,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CTable, CTableBody, CTableDataCell, CTableHead, CTableHeaderCell, CTableRow,
  CInputGroup, CInputGroupText,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilMap, cilMove, cilSettings, cilFlagAlt } from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { fmtMoneda, simboloMoneda } from '../utils/moneda'
import { NumericFormat } from 'react-number-format'
import WwGrid, { WwColumn, WwRowAction } from '../components/ww/WwGrid'
import { usePendiente } from '../components/ww/pendiente'
import GpsMapaCobradores, { GpsCobradorPunto } from '../components/GpsMapaCobradores'
import ModalPagoModulo from '../components/ModalPagoModulo'

// Badge del estado de la ruta (estadoruta.cs + adición de Rafael "Nueva"):
// el color se elige por el literal GX (Estado) y el texto visible es el amigable
// (EstadoTexto: "Cerrada"/"Abierta" — los literales "Nueva"/"Saldada" no cambian).
const BadgeEstado = (estado: string, texto: string) => (
  <CBadge
    color={estado === 'Nueva' ? 'info' : estado === 'Saldada' ? 'success' : estado === 'Off' ? 'secondary' : 'primary'}
  >
    {texto}
  </CBadge>
)

// Moneda de la grilla y de los precios de módulos: fuente única
// utils/moneda.ts (fmtMoneda — símbolo configurado por empresa + máscara GX
// "$ Z,ZZZ,ZZ9.99" con coma de miles y punto decimal). [RGM] 2026-10-02 (F)
// (antes: fmtMoneda/fmtPrecio locales con '$' fijo y sin símbolo configurable).

// Módulos del catálogo público /pago/planes-siprinia (keys de ModuloSiprinia).
interface ModuloCatalogo { key: string; precio: number; precioConImpresora?: number }
interface CatalogoSuscripcion { modulos: ModuloCatalogo[] }

const RutasPage: React.FC = () => {
  const navigate = useNavigate()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  // Catálogos para selects (Caja/Cobrador/Supervisor por NOMBRE).
  const [cajas, setCajas] = useState<any[]>([])
  const [cobradores, setCobradores] = useState<any[]>([])
  const [supervisores, setSupervisores] = useState<any[]>([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [abierto, setAbierto] = useState(false)
  const { aviso, setAviso } = usePendiente()
  // Fila en edición (zonaww.cs:2751-2753 'DO UPDATE' → webobject zona Mode="UPD")
  const [editando, setEditando] = useState<any | null>(null)
  const [errorModal, setErrorModal] = useState('')
  // [RGM] 2026-09-28 (pedido Rafael): módulo de recibo que se está pagando en
  // el modal de 3 opciones (PayPal / ePayco / comprobante) — el pago queda
  // asignado a ESA ruta.
  const [moduloPago, setModuloPago] = useState<{ tipo: string; zonaCodigo: number; etiqueta: string } | null>(null)
  // Enrutar (zonaww.cs:2394-2396 → WW enrutar.cs:1-150: grid de préstamos + "Nueva"
  // posición por fila + "Actualizar" + "Enumerar")
  const [enrutarAbierto, setEnrutarAbierto] = useState(false)
  const [enrutarZona, setEnrutarZona] = useState<number | null>(null)
  const [enrutarRows, setEnrutarRows] = useState<any[]>([])
  const [enrutarCargando, setEnrutarCargando] = useState(false)
  const [enrutarError, setEnrutarError] = useState('')
  const [nuevaPos, setNuevaPos] = useState<Record<number, string>>({})
  const [enrutarGuardando, setEnrutarGuardando] = useState<number | null>(null)
  // Drag & drop del grid Enrutar [RGM] (diseño nuevo aprobado Rafael, como el
  // Flutter "Mi Ruta"): reordenamiento HTML5 nativo + "Guardar Orden" que
  // persiste con PUT /api/v1/rutas/{routeId}/reordenar (RFC-002, Firmado RGM).
  const [enrutarOrdenGuardando, setEnrutarOrdenGuardando] = useState(false)
  const [enrutarExito, setEnrutarExito] = useState('')
  const [enrutarDrag, setEnrutarDrag] = useState<number | null>(null)
  const [enrutarOver, setEnrutarOver] = useState<number | null>(null)
  // Reversión de la Base INLINE: baseRev (ref, siempre actual) alimenta el key
  // de los inputs; baseTick fuerza el re-render tras un error (setError solo
  // no basta si el mensaje se repite y React omite el cambio de estado).
  const baseRev = useRef(0)
  const [, setBaseTick] = useState(0)
  // GPS (zonaww.cs:2411-2413 → gps.cs:1-120: "Mapa GPS Progreso Ruta")
  const [gpsAbierto, setGpsAbierto] = useState(false)
  const [gpsPuntos, setGpsPuntos] = useState<any[]>([])
  const [gpsCargando, setGpsCargando] = useState(false)
  const [gpsError, setGpsError] = useState('')
  // GPS Cobradores (gpsrutascobradores.cs — Form.Caption "GPS COBRADORES", :932)
  const [gpsCobAbierto, setGpsCobAbierto] = useState(false)
  const [gpsCobPuntos, setGpsCobPuntos] = useState<GpsCobradorPunto[]>([])
  const [gpsCobCargando, setGpsCobCargando] = useState(false)
  const [gpsCobError, setGpsCobError] = useState('')
  const [form, setForm] = useState({
    empresaCodigo: empresa, nombre: '', cajaNro: '', cobradorCodigo: '',
    supervisorCodigo: '', zonaCodigo: '', base: '', impresora: 0, sms: 0,
    libreValor1: 0, libreValor2: 75,
    libreNumerico1: 0, libreNumerico2: 10,
    libreCaracter1: '', libreCaracter2: '',
  })

  // [RGM] Precios de los módulos para el menú "Activar" por ruta: se leen del
  // catálogo público (mismo origen que el cobro del backend). Fallback a las
  // constantes actuales si la llamada falla.
  const [precios, setPrecios] = useState({ impresion: 100000, whatsappConImpresora: 100000, whatsapp: 200000 })
  useEffect(() => {
    api.get<CatalogoSuscripcion>('/pago/planes-siprinia')
      .then((c) => {
        const modulos = c?.modulos ?? []
        const impresion = modulos.find((m) => m.key === 'MOBILE_PRINTING')
        const whatsapp = modulos.find((m) => m.key === 'WHATSAPP_CONFIRMATION')
        setPrecios({
          impresion: impresion?.precio ?? 100000,
          whatsappConImpresora: whatsapp?.precioConImpresora ?? 100000,
          whatsapp: whatsapp?.precio ?? 200000,
        })
      })
      .catch(() => {}) // fallback: valores por defecto
  }, [])

  const cargar = () => {
    setCargando(true)
    // Rutas + resumen del día EN UNO (merge ATÓMICO — pedido Rafael: la
    // grilla no mostraba x Cobrar/Prestado/etc.: si el resumen llegaba antes
    // que las filas, el merge se perdía y las columnas quedaban en 0).
    Promise.all([
      api.get<any[]>(`/rutas?empresa=${empresa}`),
      api.get<any[]>(`/rutas/resumen-dia?empresa=${empresa}`).catch(() => [] as any[]),
    ])
      .then(([rutas, resumen]) => {
        const porZona = new Map((resumen ?? []).map((x: any) => [Number(x.zonaCodigo), x]))
        setRows((rutas ?? []).map((r: any) => {
          const x: any = porZona.get(Number(r.zonaCodigo))
          return {
            ...r,
            _caja: [r.cajaNro, r.cajaNombre].filter(Boolean).join(' · '),
            _supervisor: [r.supervisorCodigo, r.supervisorNombre].filter(Boolean).join(' · '),
            _cobrador: [r.cobradorCodigo, r.cobradorNombre].filter(Boolean).join(' · '),
            _cobrado: x?.cobrado ?? 0, _xcobrar: x?.xCobrar ?? 0,
            _entera: x?.entera ?? 0, _media: x?.media ?? 0,
            _porciento: x?.porcentaje ?? 0, _presto: x?.presto ?? 0,
            _gasto: x?.gasto ?? 0, _utilidad: x?.utilidad ?? 0,
            _prestado: x?.prestadoValor ?? 0, _prestadoCantidad: x?.prestadoCantidad ?? 0,
            _cartera: x?.cartera ?? 0,
          }
        }))
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
    // Catálogos para los selects de Caja/Cobrador/Supervisor.
    api.get<any[]>(`/cajas/${empresa}/resumen`).then((d) => d && setCajas(d)).catch(() => undefined)
    api.get<any[]>('/cobradores?empresa=' + empresa).then((d) => d && setCobradores(d)).catch(() => undefined)
    api.get<any[]>('/supervisores?empresa=' + empresa).then((d) => d && setSupervisores(d)).catch(() => undefined)
  }
  useEffect(cargar, [empresa])

  // ─── Base INLINE [RGM] ───────────────────────────────────────────────────────
  // Edición de la base DIRECTA en la grilla (diseño nuevo aprobado Rafael; la
  // lógica de guardado es réplica actualizabase.cs → PUT /api/route-closures/base
  // con el body { routeId, nuevaBase } — ActualizarBaseRequest, MovilController).
  // Si la ruta está cerrada HOY (estado 'Off' = existe CierreDiario de hoy,
  // estadoruta.cs) el GX rechaza con 'Ruta Cerrada'
  // (actualizarbase.panel.ios.json) → el input se deshabilita con tooltip.
  // El input es NO controlado (defaultValue); en error se revierte el valor
  // tipeado con el remount que produce baseRev (ver su definición arriba).
  const guardarBase = useCallback((fila: any, valor: string) => {
    const nueva = Number(valor)
    if (Number.isNaN(nueva) || nueva === Number(fila.zonaBase)) return
    api
      .put('/route-closures/base', { routeId: fila.zonaCodigo, nuevaBase: nueva })
      .then(() => {
        setRows((prev) => prev.map((r) => (r.zonaCodigo === fila.zonaCodigo ? { ...r, zonaBase: nueva } : r)))
        setAviso(`Base de la ruta ${fila.zonaCodigo} actualizada`)
      })
      .catch((e: any) => {
        setError(e.message)
        baseRev.current += 1
        setBaseTick((t) => t + 1)
      })
  }, [])

  // Columnas memoizadas: WwGrid resetea filtros/orden si cambia la identidad del
  // array — el render de 'zonaBase' (input inline) depende solo de guardarBase.
  const columnas = useMemo<WwColumn[]>(
    () => [
      { key: 'zonaCodigo', title: 'Cod.', type: 'number', link: true },
      { key: 'zonaNombre', title: 'Nombre' },
      {
        key: 'estadoTexto', title: 'Estado', width: 110,
        render: (row) => BadgeEstado(row.estado ?? 'On', row.estadoTexto ?? row.estado ?? '—'),
        // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO
        // visible): cuando falta estadoTexto el render cae a row.estado → el
        // exportValue espeja ese mismo fallback.
        exportValue: (row) => row.estadoTexto ?? row.estado ?? '—',
      },
      { key: '_caja', title: 'Caja' },
      { key: '_supervisor', title: 'Supervisor' },
      { key: '_cobrador', title: 'Cobrador' },
      {
        key: 'zonaBase', title: 'Base', type: 'currency',
        // Base INLINE [RGM]: input compacto editable en la propia grilla (diseño
        // nuevo aprobado Rafael). Guarda con PUT /api/route-closures/base al
        // salir del foco o con Enter. El clic en el input NO debe disparar
        // onRowClick (navegación al detalle) → stopPropagation.
        render: (row) => {
          const cerrada = row.estado === 'Off'
          const input = (
            <CFormInput
              key={`base-${row.zonaCodigo}-${row.zonaBase}-${baseRev.current}`}
              type="number"
              size="sm"
              step="0.01"
              defaultValue={row.zonaBase ?? ''}
              disabled={cerrada}
              style={{ width: 120 }}
              className={cerrada ? 'text-body-secondary' : undefined}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.stopPropagation()
                  e.currentTarget.blur()
                }
              }}
              onBlur={(e) => guardarBase(row, e.target.value)}
            />
          )
          // actualizarbase.panel.ios.json: 'Ruta Cerrada' — no se puede cambiar
          // la base de una ruta ya cerrada HOY.
          return cerrada ? (
            <CTooltip content="Ruta Cerrada — no se puede cambiar la base" placement="top">
              {input}
            </CTooltip>
          ) : (
            input
          )
        },
      },
      { key: '_cobrado', title: 'Cobrado', type: 'currency' },
      { key: '_xcobrar', title: 'x Cobrar', type: 'currency' },
      { key: '_entera', title: 'Entera', type: 'number' },
      { key: '_media', title: 'Media', type: 'number' },
      // [RGM] 2026-09-24: % recaudado (recaudado ÷ por cobrar — misma cuenta
      // del móvil; ej. 70 de 100 → 70%). Antes salía como moneda.
      { key: '_porciento', title: '% $', render: (row) => `${Number(row._porciento ?? 0)}%`, exportValue: (row) => `${Number(row._porciento ?? 0)}%` },
      { key: '_presto', title: 'Prestó', type: 'currency' },
      { key: '_gasto', title: 'Gastó', type: 'currency' },
      { key: '_utilidad', title: 'Utilidad', type: 'currency' },
      // [RGM] 2026-09-24 (pedido Rafael): Prestado = $ suma de préstamos
      // ACTIVOS / # cantidad — la columna "#" se elimina y se fusiona aquí
      // (ej. "$ 1,000.00 / 10").
      {
        key: '_prestado', title: 'Prestado', minWidth: 140,
        render: (row) => `${fmtMoneda(row._prestado ?? 0)} / ${Number(row._prestadoCantidad ?? 0)}`,
        // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO
        // visible): mismo formato "$ monto / cantidad" del render.
        exportValue: (row) => `${fmtMoneda(row._prestado ?? 0)} / ${Number(row._prestadoCantidad ?? 0)}`,
      },
      { key: '_cartera', title: 'Cartera', type: 'currency' },
      {
        key: 'modulos', title: 'Módulos', width: 190,
        // [RGM] Menú "Activar" por ruta — DOS módulos (corrección Rafael
        // 2026-09-29: "no hay solo WS"): Solo Ticket $100.000 · WS + Ticket
        // $200.000 ($100.000 con ticket ya activo). WS + Ticket enciende las
        // DOS banderas (ZonaSMS + ZonaImpresora). Al pagar y confirmar se
        // encienden en ESA ruta.
        render: (row) => {
          const impresora = Number(row.zonaImpresora ?? 0) === 1
          const sms = Number(row.zonaSMS ?? 0) === 1
          // [RGM] 2026-09-29 (pedido Rafael): pago SUBIDO pendiente de
          // confirmación → "Pendiente de confirmación" y SIN select (evita
          // pagar doble); al confirmar el superadmin quedan las luces activas.
          const pendImp = row.pendienteImpresora === true
          const pendWs = row.pendienteWhatsapp === true
          if (impresora && sms) return <CBadge color="success">WS + Ticket ✓</CBadge>
          if (pendImp || pendWs) {
            return (
              <div onClick={(e) => e.stopPropagation()}>
                <div className="d-flex flex-wrap gap-1">
                  {impresora && <CBadge color="success">Impresión ✓</CBadge>}
                  {sms && <CBadge color="success">WhatsApp ✓</CBadge>}
                  <CBadge color="warning">
                    {pendWs ? 'WS + Ticket — Pendiente de confirmación'
                      : 'Solo Ticket — Pendiente de confirmación'}
                  </CBadge>
                </div>
              </div>
            )
          }
          return (
            <div onClick={(e) => e.stopPropagation()}>
              {/* [RGM] 2026-09-29 (reporte Rafael: "En rutas no sale ... activo"
                  tras comprar SOLO WhatsApp): en estados mixtos la celda no
                  mostraba indicador — badge por módulo activo. */}
              {(impresora || sms) && (
                <div className="d-flex flex-wrap gap-1 mb-1">
                  {impresora && <CBadge color="success">Impresión ✓</CBadge>}
                  {sms && <CBadge color="success">WhatsApp ✓</CBadge>}
                </div>
              )}
              <CFormSelect
                size="sm"
                value=""
                aria-label="Activar módulo"
                onChange={(e) => { if (e.target.value) pagarModulo(row, e.target.value) }}
              >
                <option value="">Activar…</option>
                {impresora ? (
                  <option value="activar-whatsapp">
                    WS + Ticket — {fmtMoneda(precios.whatsappConImpresora)}
                  </option>
                ) : sms ? (
                  <option value="activar-impresora">
                    Solo Ticket — {fmtMoneda(precios.impresion)}
                  </option>
                ) : (
                  <>
                    <option value="activar-impresora">
                      Solo Ticket — {fmtMoneda(precios.impresion)}
                    </option>
                    <option value="activar-impresora-whatsapp">
                      WS + Ticket — {fmtMoneda(precios.impresion + precios.whatsappConImpresora)}
                    </option>
                  </>
                )}
              </CFormSelect>
            </div>
          )
        },
      },
    ],
    [guardarBase, precios],
  )

  // Activación de módulos por RUTA (réplica de activarimpresora.cs /
  // activarsms.cs): [RGM] 2026-09-28 (pedido Rafael) abre el modal con las 3
  // opciones de pago (PayPal / ePayco / comprobante) para ESA ruta; al pagar,
  // el backend enciende ZonaImpresora/ZonaSMS de la ruta.
  const pagarModulo = (row: any, tipo: string) => {
    const etiquetaTipo = tipo === 'activar-impresora' ? 'Tickets de impresión'
      : tipo === 'activar-whatsapp' ? 'Ticket por WhatsApp'
        : 'Impresión + WhatsApp'
    const nombreRuta = String(row?.zonaNombre ?? '').trim() || `Ruta ${row?.zonaCodigo ?? ''}`
    setModuloPago({
      tipo,
      zonaCodigo: Number(row?.zonaCodigo ?? 0),
      etiqueta: `${etiquetaTipo} — ${nombreRuta}`,
    })
  }

  const abrirNuevo = () => {
    setEditando(null)
    setForm({
      empresaCodigo: empresa, nombre: '', cajaNro: '', cobradorCodigo: '',
      supervisorCodigo: '', zonaCodigo: '', base: '', impresora: 0, sms: 0,
      libreValor1: 0, libreValor2: 75,
      libreNumerico1: 0, libreNumerico2: 10,
      libreCaracter1: '', libreCaracter2: '',
    })
    setAbierto(true)
  }

  // Modificar (zonaww.cs:2751-2753): carga la fila en el form y abre el modal.
  // Impresora/SMS quedan deshabilitados: el form GX los bloquea siempre
  // (zona.cs:1877-1880 — solo se activan con activarimpresora/activarsms).
  const abrirEditar = (fila: any) => {
    setErrorModal('')
    setEditando(fila)
    setForm({
      empresaCodigo: empresa,
      nombre: fila.zonaNombre ?? '',
      cajaNro: String(fila.cajaNro ?? ''),
      cobradorCodigo: String(fila.cobradorCodigo ?? ''),
      supervisorCodigo: String(fila.supervisorCodigo ?? ''),
      zonaCodigo: String(fila.zonaCodigo ?? ''),
      base: String(fila.zonaBase ?? ''),
      impresora: Number(fila.zonaImpresora ?? 0),
      sms: Number(fila.zonaSMS ?? 0),
      libreValor1: Number(fila.zonaLibreValor1 ?? 0),
      libreValor2: Number(fila.zonaLibreValor2 ?? 0),
      libreNumerico1: Number(fila.zonaLibreNumerico1 ?? 0),
      libreNumerico2: Number(fila.zonaLibreNumerico2 ?? 0),
      libreCaracter1: fila.zonaLibreCaracter1 ?? '',
      libreCaracter2: fila.zonaLibreCaracter2 ?? '',
    })
    setAbierto(true)
  }

  const crear = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorModal('')
    setError('')
    try {
      if (editando) {
        // zonaww.cs:2751-2753 — DO UPDATE: webobject zona Mode="UPD" con
        // ZonaCodigo (la PK viaja en la URL, no cambia). El backend replica
        // BC000S13 (zona_bc.cs:2209).
        await api.put(`/rutas/${editando.zonaCodigo}`, {
          empresaCodigo: form.empresaCodigo,
          nombre: form.nombre,
          cajaNro: Number(form.cajaNro),
          cobradorCodigo: Number(form.cobradorCodigo),
          supervisorCodigo: Number(form.supervisorCodigo),
          base: Number(form.base) || 0,
          libreValor1: Number(form.libreValor1) || 0,
          libreValor2: Number(form.libreValor2) || 0,
          libreNumerico1: Number(form.libreNumerico1) || 0,
          libreNumerico2: Number(form.libreNumerico2) || 0,
          libreCaracter1: form.libreCaracter1 || undefined,
          libreCaracter2: form.libreCaracter2 || undefined,
        })
      } else {
        await api.post('/rutas', {
          ...form,
          cajaNro: Number(form.cajaNro), cobradorCodigo: Number(form.cobradorCodigo),
          supervisorCodigo: Number(form.supervisorCodigo), zonaCodigo: Number(form.zonaCodigo) || 0,
          base: Number(form.base) || 0, impresora: Number(form.impresora), sms: Number(form.sms),
          libreValor1: Number(form.libreValor1) || 0,
          libreValor2: Number(form.libreValor2) || 0,
          libreNumerico1: Number(form.libreNumerico1) || 0,
          libreNumerico2: Number(form.libreNumerico2) || 0,
          libreCaracter1: form.libreCaracter1 || undefined,
          libreCaracter2: form.libreCaracter2 || undefined,
        })
      }
      setAbierto(false)
      setEditando(null)
      cargar()
    } catch (err: any) {
      setErrorModal(err.message)
      setError(err.message)
    }
  }

  const eliminar = (fila: any) => {
    // zonaww.cs:2759-2761 — DO DELETE: webobject zona Mode="DLT" con
    // ZonaCodigo. El backend replica OnDeleteControls0S25 (zona_bc.cs:709-775):
    // Cliente/Egresos/CierreDiario → 409, sin refs → DELETE (BC000S14).
    if (!window.confirm(`¿Eliminar la ruta ${fila.zonaNombre || fila.zonaCodigo}?`)) return
    api.delete(`/rutas/${fila.zonaCodigo}?empresa=${empresa}`)
      .then(() => {
        setAviso('Ruta eliminada.')
        cargar()
      })
      .catch((e: any) => setError(e.message))
  }

  // ─── Enrutar (zonaww.cs:2394-2396 → WW enrutar.cs:1-150) ────────────────────────
  const abrirEnrutar = (fila: any) => {
    setEnrutarZona(fila.zonaCodigo)
    setNuevaPos({})
    setEnrutarError('')
    setEnrutarExito('')
    setEnrutarOrdenGuardando(false)
    setEnrutarDrag(null)
    setEnrutarOver(null)
    setEnrutarAbierto(true)
    setEnrutarCargando(true)
    api
      .get<any[]>(`/rutas/${fila.zonaCodigo}/enrutar?empresa=${empresa}`)
      .then((d) => setEnrutarRows(d))
      .catch((e: any) => setEnrutarError(e.message))
      .finally(() => setEnrutarCargando(false))
  }

  // Botón "Actualizar" por fila → cambiaposicionrutaprestamo.cs:98-186, con la
  // confirmación del GX (enrutar.cs:1794 — "Esta Seguro de actualizar Posición").
  const actualizarPosicion = (row: any) => {
    const nueva = Number(nuevaPos[row.prestamoNro])
    if (!nueva || nueva === row.prestamoPosicionRuta) {
      // cambiaposicionrutaprestamo.cs:98-101 — mensaje GX exacto
      setEnrutarError('Debe colocar una posición válida')
      return
    }
    if (!window.confirm('Esta Seguro de actualizar Posición')) return
    setEnrutarGuardando(row.prestamoNro)
    api
      .post<any[]>(`/rutas/${enrutarZona}/enrutar/posicion?empresa=${empresa}`, {
        prestamoNro: row.prestamoNro,
        prestamoActual: row.prestamoPosicionRuta,
        nuevaPosicion: nueva,
        cobradorNombre: row.cobradorNombre ?? '',
      })
      .then((d) => {
        setEnrutarRows(d)
        setNuevaPos((p) => ({ ...p, [row.prestamoNro]: '' }))
      })
      .catch((e: any) => setEnrutarError(e.message))
      .finally(() => setEnrutarGuardando(null))
  }

  // Botón "Enumerar" → procenumerar.cs:72-113. Tooltip GX del botón
  // (enrutar.cs:490): "Re-Enumera Eliminando saltos de Posicion y Conservando
  // el Orden de la Ruta, Atención! Pero no pueden haber Posiciones Repetidas".
  const enumerarRuta = () => {
    if (!window.confirm('Re-Enumera Eliminando saltos de Posicion y Conservando el Orden de la Ruta, Atención! Pero no pueden haber Posiciones Repetidas')) return
    setEnrutarCargando(true)
    api
      .post<any[]>(`/rutas/${enrutarZona}/enrutar/enumerar?empresa=${empresa}`, {})
      .then((d) => {
        setEnrutarRows(d)
        setNuevaPos({})
      })
      .catch((e: any) => setEnrutarError(e.message))
      .finally(() => setEnrutarCargando(false))
  }

  // Drag & drop del grid Enrutar [RGM] (diseño nuevo aprobado Rafael, como el
  // Flutter "Mi Ruta"): al soltar una fila se reordena el array LOCAL y se
  // renumeran las posiciones visuales (prestamoPosicionRuta = índice + 1). La
  // persistencia la hace "Guardar Orden" (guardarOrden) — el flujo GX original
  // (input "Nueva" + Actualizar + Enumerar) se mantiene intacto.
  const reordenarEnrutar = (desde: number, hasta: number) => {
    setEnrutarRows((prev) => {
      const next = [...prev]
      const [movida] = next.splice(desde, 1)
      next.splice(hasta, 0, movida)
      return next.map((r, i) => ({ ...r, prestamoPosicionRuta: i + 1 }))
    })
    // Las posiciones tipeadas en "Nueva" quedaron obsoletas tras el reorden.
    setNuevaPos({})
  }

  // Botón "Guardar Orden" [RGM] → PUT /api/v1/rutas/{routeId}/reordenar
  // (RFC-002, Firmado RGM): body { clientes: [cedula...] } con las cédulas en
  // el NUEVO orden visual, SIN duplicados (un cliente con varios préstamos
  // hereda la posición a todos — ReordenarAsync). Luego recarga el grid.
  const guardarOrden = () => {
    const clientes = [...new Set(enrutarRows.map((r) => r.clienteCedula))] as number[]
    if (clientes.length < 2) {
      setEnrutarError('Debe haber al menos dos clientes para reordenar')
      return
    }
    setEnrutarOrdenGuardando(true)
    setEnrutarError('')
    setEnrutarExito('')
    api
      .put(`/v1/rutas/${enrutarZona}/reordenar`, { clientes })
      .then(() => {
        setEnrutarExito('Orden de la ruta guardado')
        setNuevaPos({})
        return api.get<any[]>(`/rutas/${enrutarZona}/enrutar?empresa=${empresa}`)
      })
      .then((d) => setEnrutarRows(d))
      .catch((e: any) => setEnrutarError(e.message))
      .finally(() => setEnrutarOrdenGuardando(false))
  }



  // ─── GPS (zonaww.cs:2411-2413 → gps.cs:1-120 "Mapa GPS Progreso Ruta") ──────────
  const abrirGps = (fila: any) => {
    setGpsError('')
    setGpsPuntos([])
    setGpsAbierto(true)
    setGpsCargando(true)
    api
      .get<any>(`/rutas/${fila.zonaCodigo}/gps?empresa=${empresa}`)
      .then((d) => setGpsPuntos(d.puntos ?? []))
      .catch((e: any) => setGpsError(e.message))
      .finally(() => setGpsCargando(false))
  }

  // ─── GPS Cobradores (gpsrutascobradores.cs — "GPS COBRADORES") ─────────────────
  // Último abono de HOY por zona (H002J2/H002J4, gpsrutascobradores.cs:1162-1163):
  // la posición actual de cada cobrador, en el mapa real de Google Maps.
  const cargarGpsCobradores = () => {
    api
      .get<any>(`/rutas/gps-cobradores?empresa=${empresa}`)
      .then((d) => setGpsCobPuntos(d.puntos ?? []))
      .catch((e: any) => setGpsCobError(e.message))
      .finally(() => setGpsCobCargando(false))
  }

  const abrirGpsCobradores = () => {
    setGpsCobError('')
    setGpsCobPuntos([])
    setGpsCobAbierto(true)
    setGpsCobCargando(true)
    cargarGpsCobradores()
  }

  // TIEMPO REAL: mientras el panel GPS Cobradores está abierto se refresca
  // cada 60s (el tracking del móvil reporta cada 2 min — el mapa se actualiza
  // solo, sin recargar la página).
  useEffect(() => {
    if (!gpsCobAbierto) return
    const timer = setInterval(() => cargarGpsCobradores(), 60_000)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpsCobAbierto, empresa])

  const verHoy = (fila: any) => {
    // La acción "Hoy" abre el DETALLE de la ruta
    // (RutaViewPage con el reporte RptTotalRuta), igual que tocar la fila.
    // Antes mostraba un modal con la grilla resumen-dia (no funcional) — eliminado.
    navigate(`/rutas/${fila.zonaCodigo}`)
  }

  const irPrestamos = (fila: any) => {
    navigate(`/prestamos?ruta=${encodeURIComponent(String(fila.zonaNombre ?? '').trim())}`)
  }

  // Iconos de fila en el ORDEN pedido por Rafael (2026-08-27): Hoy, GPS,
  // Prestamos, Enrutar — visibles en la columna de acciones (inline-end),
  // con la columna ampliada a 150px (4 iconos + ⋮). Recibos ELIMINADO del
  // listado y de las acciones por decisión de Rafael.
  const rowActions: WwRowAction[] = [
    { id: 'hoy', label: 'Hoy', icon: 'cilCalendar', placement: 'inline-end', onClick: verHoy },
    { id: 'gps', label: 'GPS', icon: 'cilMap', placement: 'inline-end', onClick: abrirGps },
    { id: 'prestamos', label: 'Prestamos', icon: 'cilDollar', placement: 'inline-end', onClick: irPrestamos },
    { id: 'enrutar', label: 'Enrutar', icon: 'cilShare', placement: 'inline-end', onClick: abrirEnrutar },
  ]

  const [totalReg, setTotalReg] = useState(rows.length)
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Rutas</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
        {/* "GPS Cobradores" — réplica del proc
            gpsrutascobradores.cs (Form.Caption "GPS COBRADORES", :932): mapa con
            la posición actual de cada cobrador (último abono del día por zona). */}
        <CCol xs="auto" className="text-end align-self-center">
          <CButton color="outline-primary" onClick={abrirGpsCobradores}>
            <CIcon icon={cilMap} className="me-2" />
            GPS Cobradores
          </CButton>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      <WwGrid
        title="Rutas"
        columns={columnas}
        rows={rows}
        onTotalChange={setTotalReg}
        loading={cargando}
        onAgregar={abrirNuevo}
        onEditar={abrirEditar}
        onEliminar={eliminar}
        rowActions={rowActions}
        accionesMinWidth={150}
        // TOCAR LA FILA abre el detalle de la ruta
        // (RutaViewPage con el reporte RptTotalRuta), igual que la acción "Hoy".
        onRowClick={(row) => navigate(`/rutas/${row.zonaCodigo}`)}
        onRowLink={(colKey, row) => {
          if (colKey === 'zonaCodigo') navigate(`/rutas/${row.zonaCodigo}`)
        }}
      />

      <CModal visible={abierto} onClose={() => { setAbierto(false); setEditando(null) }}>
        <CModalHeader>
          <CModalTitle>{editando ? 'Modificar ruta' : 'Nueva ruta'}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          <CForm ref={formRef} onSubmit={crear}>
            {/* ── TARJETA 1 — DATOS DE LA RUTA ── */}
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilMap} size="sm" /> Datos de la Ruta
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  {/* Mayúsculas (patrón GX: nombres en BD en mayúsculas) */}
                  <CCol md={6}><CFormLabel>Nombre</CFormLabel><CFormInput value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })} /></CCol>
                  <CCol md={6}>
                    {/* Caja por NOMBRE (el backend recibe cajaNro) */}
                    <CFormLabel>Caja</CFormLabel>
                    <CFormSelect value={form.cajaNro} onChange={(e) => setForm({ ...form, cajaNro: e.target.value })}>
                      <option value="">Seleccione...</option>
                      {cajas.map((cj) => <option key={cj.cajaNro} value={cj.cajaNro}>{cj.cajaNombre}</option>)}
                    </CFormSelect>
                  </CCol>
                  <CCol md={6}>
                    {/* Cobrador por NOMBRE */}
                    <CFormLabel>Cobrador</CFormLabel>
                    <CFormSelect value={form.cobradorCodigo} onChange={(e) => setForm({ ...form, cobradorCodigo: e.target.value })}>
                      <option value="">Seleccione...</option>
                      {cobradores.map((cb) => <option key={cb.cobradorCodigo} value={cb.cobradorCodigo}>{cb.nombre}</option>)}
                    </CFormSelect>
                  </CCol>
                  <CCol md={6}>
                    {/* Supervisor por NOMBRE */}
                    <CFormLabel>Supervisor</CFormLabel>
                    <CFormSelect value={form.supervisorCodigo} onChange={(e) => setForm({ ...form, supervisorCodigo: e.target.value })}>
                      <option value="">Seleccione...</option>
                      {supervisores.map((sp) => <option key={sp.supervisorCodigo} value={sp.supervisorCodigo}>{sp.supervisorNombre}</option>)}
                    </CFormSelect>
                  </CCol>
                  {/* ZonaCodigo oculto: lo genera el backend (zonaww.cs:1909
                      edtZonaCodigo_Enabled=0 — no se muestra, decisión Rafael). */}
                  <CCol md={6}><CFormLabel>Base</CFormLabel>
                    {/* [RGM] 2026-10-02 (moneda global): prefijo + miles. */}
                    <CInputGroup>
                      <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                      <NumericFormat
                        className="form-control"
                        value={form.base}
                        onValueChange={(v) => setForm({ ...form, base: v.value })}
                        thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                        allowNegative={false} inputMode="decimal"
                      />
                    </CInputGroup>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>

            {/* ── TARJETA 2 — METAS (ZonaLibreValor1/2, ZonaLIbreNumerico2 —
                réplica gethomesampledatacontrol.cs:100 / gethomesampledatarutas.cs:122-130) ── */}
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilFlagAlt} size="sm" /> Metas
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  <CCol md={6}><CFormLabel>Meta Rendimientos Mes</CFormLabel><CFormInput type="number" step="0.01" value={form.libreValor1} onChange={(e) => setForm({ ...form, libreValor1: Number(e.target.value) })} /></CCol>
                  <CCol md={6}><CFormLabel>Meta % Renovación</CFormLabel><CFormInput type="number" step="0.01" value={form.libreValor2} onChange={(e) => setForm({ ...form, libreValor2: Number(e.target.value) })} /></CCol>
                  <CCol md={6}><CFormLabel>Meta Nuevos</CFormLabel><CFormInput type="number" value={form.libreNumerico2} onChange={(e) => setForm({ ...form, libreNumerico2: Number(e.target.value) })} /></CCol>
                </CRow>
              </CCardBody>
            </CCard>

            {/* ── TARJETA 3 — CONFIGURACIÓN ── */}
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilSettings} size="sm" /> Configuración
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  {/* ZonaImpresora = SI/NO (zonaww.cs:3531-3532); indicador del
                      estado de impresión de la ruta (activarimpresora.cs); el
                      NOMBRE de la impresora (ZonaLibreCaracter2) se muestra
                      debajo cuando está activada — decisión Rafael. */}
                  <CCol md={6}>
                    <CFormLabel>Impresión</CFormLabel>
                    <CFormSelect value={String(form.impresora)} disabled>
                      <option value="0">No</option>
                      <option value="1">Si</option>
                    </CFormSelect>
                  </CCol>
                  {/* ZonaSMS = SI/NO (zonaww.cs:3552-3553); WhatsApp COMPLETO
                      (no "WS"); NO editable (cajazonawc.cs:1677 — activarsms) */}
                  <CCol md={6}>
                    <CFormLabel>WhatsApp</CFormLabel>
                    <CFormSelect value={String(form.sms)} disabled>
                      <option value="0">No</option>
                      <option value="1">Si</option>
                    </CFormSelect>
                  </CCol>
                  {/* [RGM] Menú "Activar" de módulos (mismo criterio de la
                      grilla): solo al EDITAR una ruta — el GX activa por los
                      procs de pago (zona.cs:1877-1880), no desde el form. */}
                  {editando && (
                    <CCol md={12}>
                      {Number(form.impresora) === 1 && Number(form.sms) === 1 ? (
                        <CBadge color="success">WS + Ticket ✓</CBadge>
                      ) : (editando?.pendienteImpresora === true || editando?.pendienteWhatsapp === true) ? (
                        // [RGM] 2026-09-29 (pedido Rafael): pago SUBIDO pendiente
                        // de confirmación → chips, SIN select (no pagar doble).
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          {Number(form.impresora) === 1 && <CBadge color="success">Impresión ✓</CBadge>}
                          {Number(form.sms) === 1 && <CBadge color="success">WhatsApp ✓</CBadge>}
                          <CBadge color="warning">
                            {editando?.pendienteWhatsapp === true
                              ? 'WS + Ticket — Pendiente de confirmación'
                              : 'Solo Ticket — Pendiente de confirmación'}
                          </CBadge>
                        </div>
                      ) : (
                        <CFormSelect
                          size="sm"
                          value=""
                          aria-label="Activar módulo"
                          style={{ maxWidth: 320 }}
                          onChange={(e) => { if (e.target.value) pagarModulo(editando, e.target.value) }}
                        >
                          <option value="">Activar…</option>
                          {Number(form.impresora) === 1 ? (
                            <option value="activar-whatsapp">
                              WS + Ticket — {fmtMoneda(precios.whatsappConImpresora)}
                            </option>
                          ) : Number(form.sms) === 1 ? (
                            <option value="activar-impresora">
                              Solo Ticket — {fmtMoneda(precios.impresion)}
                            </option>
                          ) : (
                            <>
                              <option value="activar-impresora">
                                Solo Ticket — {fmtMoneda(precios.impresion)}
                              </option>
                              <option value="activar-impresora-whatsapp">
                                WS + Ticket — {fmtMoneda(precios.impresion + precios.whatsappConImpresora)}
                              </option>
                            </>
                          )}
                        </CFormSelect>
                      )}
                    </CCol>
                  )}
                  {/* ZonaLibreCaracter2 = NOMBRE de la impresora BT de la ruta
                      (opcionesticket_panel: &Zonalibrecaracter2.trim() →
                      Printer.PrinterName); editable SOLO si la impresión está
                      activada (ZonaImpresora == 1). */}
                  {Number(form.impresora) === 1 && (
                    <CCol md={12}>
                      <CFormLabel>Nombre de impresora</CFormLabel>
                      <CFormInput value={form.libreCaracter2} placeholder="Como aparece en el Bluetooth del celular" onChange={(e) => setForm({ ...form, libreCaracter2: e.target.value })} />
                    </CCol>
                  )}
                </CRow>
              </CCardBody>
            </CCard>

          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => { setAbierto(false); setEditando(null) }}>Cancelar</CButton>
          <CButton color="primary" type="button" onClick={() => formRef.current?.requestSubmit()}>{editando ? 'Guardar cambios' : 'Crear ruta'}</CButton>
        </CModalFooter>
      </CModal>

      {/* ─── Enrutar (zonaww.cs:2394-2396 → WW enrutar.cs:1-150) ─────────────────── */}
      <CModal
        visible={enrutarAbierto}
        onClose={() => setEnrutarAbierto(false)}
        size="xl"
      >
        <CModalHeader>
          <CModalTitle>Enrutar</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {enrutarError && <CAlert color="danger" dismissible onClose={() => setEnrutarError('')}>{enrutarError}</CAlert>}
          {enrutarExito && <CAlert color="success" dismissible onClose={() => setEnrutarExito('')}>{enrutarExito}</CAlert>}
          {enrutarCargando ? (
            <div className="text-center py-4"><CSpinner color="primary" /></div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex gap-2 align-items-center">
                  {/* Botón "Enumerar" (enrutar.cs:490) */}
                  <CButton color="primary" onClick={enumerarRuta} disabled={enrutarRows.length === 0}>
                    Enumerar
                  </CButton>
                  {/* "Guardar Orden" [RGM]: persiste el orden del drag & drop →
                      PUT /api/v1/rutas/{zona}/reordenar (RFC-002, Firmado RGM). */}
                  <CButton
                    color="outline-primary"
                    onClick={guardarOrden}
                    disabled={enrutarOrdenGuardando || enrutarRows.length === 0}
                  >
                    {enrutarOrdenGuardando ? 'Guardando...' : 'Guardar Orden'}
                  </CButton>
                </div>
                {/* Condición GX (enrutar.cs:497) */}
                <small className="text-body-secondary">
                  Para enumerar la primera posición debe ser 1 y no 0, y no puede haber posiciones repetidas
                </small>
              </div>
              <CTable hover responsive>
                <CTableHead>
                  <CTableRow>
                    {/* Columnas del grid GX (enrutar.cs:1621-1656) + handle de
                        arrastre [RGM] */}
                    <CTableHeaderCell style={{ width: 40 }}></CTableHeaderCell>
                    <CTableHeaderCell className="text-end">Prestamo</CTableHeaderCell>
                    <CTableHeaderCell className="text-end">Actual</CTableHeaderCell>
                    <CTableHeaderCell className="text-end">Nueva</CTableHeaderCell>
                    <CTableHeaderCell></CTableHeaderCell>
                    <CTableHeaderCell>Barrio</CTableHeaderCell>
                    <CTableHeaderCell>Dirección</CTableHeaderCell>
                    <CTableHeaderCell>Negocio</CTableHeaderCell>
                    <CTableHeaderCell>Nombre</CTableHeaderCell>
                    <CTableHeaderCell>Teléfono</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {enrutarRows.length === 0 && (
                    <CTableRow>
                      <CTableDataCell colSpan={10} className="text-center text-body-secondary">
                        No hay préstamos para enrutar
                      </CTableDataCell>
                    </CTableRow>
                  )}
                  {enrutarRows.map((row, i) => (
                    <CTableRow
                      key={row.prestamoNro}
                      // Drag & drop [RGM] (HTML5 nativo, sin dependencias): el
                      // drop en otra fila reordena el array local (reordenarEnrutar).
                      onDragOver={(e) => {
                        // preventDefault habilita el soltar en la fila
                        if (enrutarDrag !== null && enrutarDrag !== i) {
                          e.preventDefault()
                          setEnrutarOver(i)
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault()
                        if (enrutarDrag !== null && enrutarDrag !== i) reordenarEnrutar(enrutarDrag, i)
                        setEnrutarDrag(null)
                        setEnrutarOver(null)
                      }}
                      style={
                        enrutarOver === i && enrutarDrag !== null && enrutarDrag !== i
                          ? { background: 'var(--cui-primary-bg-subtle, #cfe2ff)' }
                          : undefined
                      }
                    >
                      {/* Handle de arrastre [RGM]: SOLO el icono inicia el drag —
                          no interfiere con el input "Nueva" ni el link de maps. */}
                      <CTableDataCell style={{ width: 40 }}>
                        <span
                          draggable
                          title="Arrastrar para reordenar"
                          className="d-inline-block"
                          style={{ cursor: 'grab', color: 'var(--cui-body-color)' }}
                          onDragStart={(e) => {
                            setEnrutarDrag(i)
                            e.dataTransfer.effectAllowed = 'move'
                            // Firefox exige setData para iniciar el arrastre
                            e.dataTransfer.setData('text/plain', String(i))
                          }}
                          onDragEnd={() => {
                            setEnrutarDrag(null)
                            setEnrutarOver(null)
                          }}
                        >
                          <CIcon icon={cilMove} />
                        </span>
                      </CTableDataCell>
                      <CTableDataCell className="text-end">{row.prestamoNro}</CTableDataCell>
                      <CTableDataCell className="text-end">{row.prestamoPosicionRuta}</CTableDataCell>
                      <CTableDataCell className="text-end" style={{ width: 90 }}>
                        <CFormInput
                          type="number"
                          size="sm"
                          value={nuevaPos[row.prestamoNro] ?? ''}
                          placeholder={String(row.prestamoPosicionRuta)}
                          onChange={(e) => setNuevaPos((p) => ({ ...p, [row.prestamoNro]: e.target.value }))}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        {/* "Actualizar" (enrutar.cs:1501) → cambiaposicionrutaprestamo.cs:98-186 */}
                        <CButton
                          color="primary"
                          size="sm"
                          disabled={enrutarGuardando === row.prestamoNro}
                          onClick={() => actualizarPosicion(row)}
                        >
                          {enrutarGuardando === row.prestamoNro ? '...' : 'Actualizar'}
                        </CButton>
                      </CTableDataCell>
                      <CTableDataCell>{row.clienteBarrioDireccion}</CTableDataCell>
                      {/* Dirección con link a Google Maps (enrutar.cs:1517) */}
                      <CTableDataCell>
                        {row.clienteDireccion && (
                          <a
                            href={`https://maps.google.com/maps?q=${encodeURIComponent(row.clienteDireccion)}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {row.clienteDireccion}
                          </a>
                        )}
                      </CTableDataCell>
                      <CTableDataCell>{row.clienteNegocio}</CTableDataCell>
                      <CTableDataCell>{row.clienteNombre}</CTableDataCell>
                      <CTableDataCell>{row.clienteTelefono}</CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setEnrutarAbierto(false)}>Cerrar</CButton>
        </CModalFooter>
      </CModal>

      {/* ─── GPS (zonaww.cs:2411-2413 → gps.cs:1-120 "Mapa GPS Progreso Ruta") ──── */}
      <CModal
        visible={gpsAbierto}
        onClose={() => setGpsAbierto(false)}
        size="lg"
      >
        <CModalHeader>
          <CModalTitle>GPS</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {gpsError && <CAlert color="danger">{gpsError}</CAlert>}
          {gpsCargando ? (
            <div className="text-center py-4"><CSpinner color="primary" /></div>
          ) : (
            <>
              {/* Leyenda del mapa GX (gps.cs:1-120:1000) */}
              <div className="d-flex flex-wrap gap-3 mb-3 small">
                <span><span className="d-inline-block rounded-circle me-1" style={{ width: 10, height: 10, background: 'green' }} />Verde: Pagó</span>
                <span><span className="d-inline-block rounded-circle me-1" style={{ width: 10, height: 10, background: 'blue' }} />Azul: Atraso</span>
                <span><span className="d-inline-block rounded-circle me-1" style={{ width: 10, height: 10, background: 'red' }} />Rojo: Por Cobrar</span>
                <span><span className="d-inline-block rounded-circle me-1" style={{ width: 10, height: 10, background: 'orange' }} />Naranja: Pagó Atraso</span>
              </div>
              {/* El MAPA SIEMPRE visible (decisión Rafael): sin puntos se
                  muestra el mapa igual, con el aviso debajo. */}
              <GpsMapaCobradores
                puntos={gpsPuntos.map((p) => ({
                  zonaCodigo: 0,
                  zonaNombre: '',
                  lat: String(p.lat ?? ''),
                  lng: String(p.lng ?? ''),
                  titulo: String(p.titulo ?? ''),
                  descripcion: String(p.descripcion ?? ''),
                  linkDsc: String(p.linkDsc ?? ''),
                  street: String(p.street ?? ''),
                  color: p.icono ?? 'verde',
                }))}
              />
              {gpsPuntos.length === 0 && (
                <div className="text-center py-2 text-body-secondary">Sin ubicaciones para hoy</div>
              )}
              {gpsPuntos.length > 0 && (
                <CTable hover responsive>
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell style={{ width: 90 }}></CTableHeaderCell>
                      <CTableHeaderCell>Cliente</CTableHeaderCell>
                      <CTableHeaderCell>Negocio</CTableHeaderCell>
                      <CTableHeaderCell>Valor / Hora</CTableHeaderCell>
                      <CTableHeaderCell>Ubicación</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {gpsPuntos.map((p, i) => (
                      <CTableRow key={i}>
                        <CTableDataCell>
                          <span className="d-inline-block rounded-circle" style={{ width: 12, height: 12, background: p.icono }} />
                        </CTableDataCell>
                        <CTableDataCell>{p.titulo}</CTableDataCell>
                        <CTableDataCell>{p.descripcion}</CTableDataCell>
                        <CTableDataCell>{[p.linkDsc, p.street].filter(Boolean).join(' · ')}</CTableDataCell>
                        <CTableDataCell>
                          <a href={`https://www.google.com/maps?q=${encodeURIComponent(`${p.lat},${p.lng}`)}`} target="_blank" rel="noreferrer">
                            {p.lat}, {p.lng}
                          </a>
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              )}
            </>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setGpsAbierto(false)}>Cerrar</CButton>
        </CModalFooter>
      </CModal>

      {/* ─── GPS Cobradores (gpsrutascobradores.cs — "GPS COBRADORES") ────────────── */}
      <CModal
        visible={gpsCobAbierto}
        onClose={() => setGpsCobAbierto(false)}
        size="lg"
      >
        <CModalHeader>
          {/* Caption del WW GX (gpsrutascobradores.cs:932) */}
          <CModalTitle>GPS COBRADORES</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {gpsCobError && <CAlert color="danger" dismissible onClose={() => setGpsCobError('')}>{gpsCobError}</CAlert>}
          {gpsCobCargando ? (
            <div className="text-center py-4"><CSpinner color="primary" /></div>
          ) : (
            <>
              {/* Mapa real de Google Maps (réplica del Googlemapcontrol1 con
                  GoogleApiKey, gpsrutascobradores.cs:817) */}
              <GpsMapaCobradores puntos={gpsCobPuntos} />
              {/* Leyenda del punto GX: marcador VERDE (gpsrutascobradores.cs:705) */}
              <div className="d-flex flex-wrap gap-3 mt-3 mb-3 small">
                <span>
                  <span className="d-inline-block rounded-circle me-1" style={{ width: 10, height: 10, background: 'green' }} />
                  Verde: Último cobro del día (posición actual del cobrador)
                </span>
              </div>
              {gpsCobPuntos.length === 0 ? (
                <div className="text-center py-4 text-body-secondary">Sin ubicaciones para hoy</div>
              ) : (
                <CTable hover responsive>
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell>Zona</CTableHeaderCell>
                      <CTableHeaderCell>Cliente</CTableHeaderCell>
                      <CTableHeaderCell>Negocio</CTableHeaderCell>
                      <CTableHeaderCell>Valor / Hora</CTableHeaderCell>
                      <CTableHeaderCell>Recibo</CTableHeaderCell>
                      <CTableHeaderCell>Ubicación</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {gpsCobPuntos.map((p, i) => (
                      <CTableRow key={i}>
                        <CTableDataCell>{p.zonaCodigo} · {p.zonaNombre}</CTableDataCell>
                        <CTableDataCell>{p.titulo}</CTableDataCell>
                        <CTableDataCell>{p.descripcion}</CTableDataCell>
                        <CTableDataCell>{[p.linkDsc, p.street].filter(Boolean).join(' · ')}</CTableDataCell>
                        <CTableDataCell>
                          {/* Recibo (geturl.cs:84 → aticket?AbonoNro → ruta web /abonos/:nro) */}
                          {p.abonoNro ? <Link to={`/abonos/${p.abonoNro}`}>{p.abonoNro}</Link> : ''}
                        </CTableDataCell>
                        <CTableDataCell>
                          <a href={`https://www.google.com/maps?q=${encodeURIComponent(`${p.lat},${p.lng}`)}`} target="_blank" rel="noreferrer">
                            {p.lat}, {p.lng}
                          </a>
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              )}
            </>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setGpsCobAbierto(false)}>Cerrar</CButton>
        </CModalFooter>
      </CModal>

      {/* [RGM] 2026-09-28 (pedido Rafael): pago del módulo de recibo por RUTA
          con las 3 opciones (PayPal / ePayco / comprobante). Al cerrar se
          recargan las rutas para refrescar los estados de módulos. */}
      {moduloPago && (
        <ModalPagoModulo
          visible
          tipo={moduloPago.tipo}
          zonaCodigo={moduloPago.zonaCodigo}
          etiqueta={moduloPago.etiqueta}
          onClose={() => { setModuloPago(null); cargar() }}
          onPagado={cargar}
        />
      )}
    </>
  )
}

export default RutasPage
